<form [formGroup]="form" class="p-fluid form-assignment-container">
    <!-- Patient Information Section -->
    <div class="mb-4" *ngIf="patientInfo">
        <div class="patient-info-section">
            <div class="flex align-items-center mb-3">
                <i class="pi pi-user text-blue-500 mr-2" style="font-size: 1.5rem;"></i>
                <h3 class="text-900 font-semibold m-0">Patient Information</h3>
            </div>
            <div class="grid">
                <div class="col-12 lg:col-8">
                    <div class="text-900 font-medium text-lg mb-2">
                        {{ patientInfo.firstName }} {{ patientInfo.lastName }}
                    </div>
                    <div class="flex flex-wrap gap-4 text-600">
                        <div class="flex align-items-center">
                            <i class="pi pi-envelope mr-2"></i>{{ patientInfo.email }}
                        </div>
                        <div class="flex align-items-center" *ngIf="patientInfo.dateOfBirth">
                            <i class="pi pi-calendar mr-2"></i>{{ calculateAge(patientInfo.dateOfBirth) }} years old
                        </div>
                        <div class="flex align-items-center" *ngIf="patientInfo.sex?.displayName">
                            <i class="pi pi-users mr-2"></i>{{ patientInfo.sex.displayName }}
                        </div>
                    </div>
                </div>
                <div class="col-12 lg:col-4 text-right">
                    <div class="text-sm text-500" *ngIf="patientInfo.dateOfBirth">
                        <strong>DOB:</strong> {{ formatDate(patientInfo.dateOfBirth) }}
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Expired Form Card -->
    <div class="border-round shadow-2 p-4 mb-4 bg-red-500 border-2 border-red-600" *ngIf="isFormExpired()">
        <div class="flex align-items-center mb-4">
            <i class="pi pi-exclamation-triangle text-0 mr-2 text-xl"></i>
            <h4 class="text-0 font-semibold m-0">{{ 'FORM_EXPIRED' | translate }} {{ getSelectedFormName() }} </h4>

        </div>

        <div class="flex align-items-center mb-3">
            <span class="text-0">
                {{ 'FORM_EXPIRED_MESSAGE' | translate }}
                <span *ngIf="patientInfo" class="font-semibold">
                    {{ patientInfo.firstName }} {{ patientInfo.lastName }}
                </span>
                has expired and is no longer available for completion.
            </span>
        </div>

        <div class="mb-4">
            <div class="text-0">
                <span class="font-semibold">{{ 'EXPIRED_ON' | translate }}:</span>
                <span class="ml-2">{{ getFormattedExpiryDate() }}</span>
            </div>
        </div>

        <div>
            <div class="text-0 font-medium mb-3">{{ 'EXTEND_EXPIRY' | translate }}:</div>
            <div class="flex flex-wrap gap-2 align-items-center">
                <p-button [label]="'1_HOUR' | translate" severity="contrast" size="small"
                    (onClick)="extendFormExpiry('1_HOUR')" [loading]="isExtending">
                </p-button>

                <p-button [label]="'1_DAY' | translate" severity="contrast" size="small"
                    (onClick)="extendFormExpiry('1_DAY')" [loading]="isExtending">
                </p-button>

                <p-button [label]="'7_DAYS' | translate" severity="contrast" size="small"
                    (onClick)="extendFormExpiry('7_DAYS')" [loading]="isExtending">
                </p-button>

                <p-button [label]="'28_DAYS' | translate" severity="contrast" size="small"
                    (onClick)="extendFormExpiry('28_DAYS')" [loading]="isExtending">
                </p-button>

                <!-- Separator -->
                <div class="border-left-2 border-white-alpha-50 ml-2 mr-2" style="height: 2rem;"></div>

                <!-- Delete Button -->
                <p-button label="Delete" icon="pi pi-trash" severity="secondary" size="small" outlined
                    (onClick)="confirmDeleteAssignment()" [loading]="isDeleting" styleClass="text-white border-white">
                </p-button>
            </div>
        </div>
    </div>

    <!-- Completed Form Card -->
    <div class="border-round shadow-2 p-4 mb-4 bg-green-500 border-2 border-green-600" *ngIf="isFormCompleted()">
        <div class="flex align-items-center mb-4">
            <i class="pi pi-check-circle text-0 mr-2 text-xl"></i>
            <h4 class="text-0 font-semibold m-0">{{ 'FORM_COMPLETED' | translate }} {{ getSelectedFormName() }}</h4>
        </div>

        <div class="flex align-items-center mb-3">
            <span class="text-0">
                {{ 'FORM_COMPLETED_MESSAGE' | translate }}
                <span *ngIf="patientInfo" class="font-semibold">
                    {{ patientInfo.firstName }} {{ patientInfo.lastName }}
                </span>
                has successfully completed this form.
            </span>
        </div>

        <div class="grid">
            <div class="col-12 md:col-6">
                <div class="text-0 mb-2">
                    <span class="font-semibold">{{ 'ASSIGNED_ON' | translate }}:</span>
                    <span class="ml-2">{{ getFormattedAssignedDate() }}</span>
                </div>
                <div class="text-0 mb-2">
                    <span class="font-semibold">{{ 'COMPLETED_ON' | translate }}:</span>
                    <span class="ml-2">{{ getFormattedCompletedDate() }}</span>
                </div>
                <div class="text-0 mb-2" *ngIf="getFormattedFirstAccessedDate()">
                    <span class="font-semibold">{{ 'FIRST_ACCESSED_ON' | translate }}:</span>
                    <span class="ml-2">{{ getFormattedFirstAccessedDate() }}</span>
                </div>
            </div>
            <div class="col-12 md:col-6">
                <div class="text-0 mb-2" *ngIf="getFillingMethodDisplay()">
                    <span class="font-semibold">{{ 'FILLING_METHOD' | translate }}:</span>
                    <span class="ml-2">{{ getFillingMethodDisplay() }}</span>
                </div>
                <div class="text-0 mb-2">
                    <span class="font-semibold">{{ 'COMPLETION_TIME' | translate }}:</span>
                    <span class="ml-2">{{ getCompletionDuration() }}</span>
                </div>
                <div class="text-0 mb-2" *ngIf="form.get('filledByUserId')?.value">
                    <span class="font-semibold">{{ 'FILLED_BY' | translate }}:</span>
                    <span class="ml-2">{{ form.get('filledByUserId')?.value }}</span>
                </div>
            </div>
        </div>

        <div class="mt-4" *ngIf="form.get('responseData')?.value">
            <div class="text-0 font-medium mb-3">{{ 'ACTIONS' | translate }}:</div>
            <div class="flex flex-wrap gap-2 align-items-center">
                <p-button [label]="'VIEW_RESPONSES' | translate" severity="contrast" size="small" icon="pi pi-eye"
                    (onClick)="viewFormResponses()">
                </p-button>

                <!-- Download dropdown with multiple format options -->
                <p-splitButton [label]="'DOWNLOAD_CSV' | translate" severity="contrast" size="small"
                    icon="pi pi-download" (onClick)="downloadFormResponsesAsCsv()" [model]="downloadMenuItems">
                </p-splitButton>

                <p-button [label]="'DOWNLOAD_ALL_FILES' | translate" severity="contrast" size="small"
                    icon="pi pi-file-export" (onClick)="downloadAllFiles()">
                </p-button>

                <!-- Separator -->
                <div class="border-left-2 border-white-alpha-50 ml-2 mr-2" style="height: 2rem;"></div>

                <!-- Delete Button -->
                <p-button label="Delete" icon="pi pi-trash" severity="secondary" size="small" outlined
                    (onClick)="confirmDeleteAssignment()" [loading]="isDeleting" styleClass="text-white border-white">
                </p-button>
            </div>
        </div>
    </div>

    <!-- Form Fill Links Card -->
    <div class="surface-card border-round shadow-1 p-4 mb-4" *ngIf="shouldShowFormFillLinks()">
        <div class="flex align-items-center justify-content-between mb-4">
            <div class="flex align-items-center">
                <i class="pi pi-link text-primary mr-2" style="font-size: 1.2rem;"></i>
                <div class="flex flex-column">
                    <h4 class="text-900 font-semibold m-0">Form Fill Links</h4>
                    <div class="text-900 font-medium text-lg mt-2" *ngIf="getSelectedFormName()">
                        {{ getSelectedFormName() }}
                    </div>
                </div>
            </div>

            <!-- Status Indicator -->
            <p-tag [value]="getStatusDisplayValue(currentFormAssignmentStatus)"
                [severity]="getStatusSeverity(currentFormAssignmentStatus)"
                [style]="{ 'min-width': '80px', 'text-align': 'center' }">
            </p-tag>
        </div>

        <div class="grid">
            <!-- QR Code Section -->
            <div class="col-12 md:col-4">
                <div class="text-center">
                    <div class="flex justify-content-center">
                        <canvas #qrCodeCanvas></canvas>
                    </div>
                </div>
            </div>

            <!-- Links and Actions Section -->
            <div class="col-12 md:col-8">
                <div class="flex flex-column gap-4">
                    <!-- Direct Link -->
                    <div class="field">
                        <label class="block text-900 font-semibold mb-2">Direct Link</label>
                        <div class="p-inputgroup">
                            <input pInputText [value]="formFillUrls?.link || ''" [readonly]="true"
                                class="w-full mr-4" />
                            <app-global-copy-to-clipboard [text]="formFillUrls?.link || ''">
                            </app-global-copy-to-clipboard>
                        </div>
                    </div>

                    <!-- Action Buttons -->
                    <div class="field">
                        <label class="block text-900 font-semibold mb-2">Actions</label>
                        <div class="flex gap-2">
                            <div>
                                <p-button (click)="sendEmail()" styleClass="p-button-bold">
                                    <i class="pi pi-envelope mr-2"></i>
                                    Send Email
                                </p-button>
                            </div>


                            <div>
                                <p-button (click)="sendRCS()" styleClass="p-button-bold p-button-success">
                                    <i class="pi pi-comment mr-2"></i>
                                    Send via RCS
                                </p-button>

                            </div>

                            <div>
                                <p-button (click)="fillOutNow()" styleClass="p-button-bold p-button-info">
                                    <i class="pi pi-pencil mr-2"></i>
                                    Fill Out Now
                                </p-button>
                            </div>


                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- More Details Section (Collapsible) -->
    <div *ngIf="crudMode !== CrudFormAssignmentMode.CREATE">
        <p-accordion>
            <p-accordionTab>
                <ng-template pTemplate="header">
                    <div class="flex align-items-center gap-2">
                        <i class="pi pi-info-circle text-primary"></i>
                        <span class="text-900 font-semibold">More Details</span>
                        <span class="text-500 text-sm">(Assignment Details, Status, Timeline & Response Data)</span>
                    </div>
                </ng-template>

                <!-- Assignment Details Card -->
                <div class="surface-card border-round shadow-1 p-4 mb-4">
                    <div class="flex align-items-center mb-4">
                        <i class="pi pi-file-edit text-primary mr-2" style="font-size: 1.2rem;"></i>
                        <h4 class="text-900 font-semibold m-0">Assignment Details</h4>
                    </div>

                    <div class="formgrid grid">
                        <!-- Assignment ID (Read-only) -->
                        <div class="field col-12 md:col-6">
                            <label for="formId" class="block text-900 font-medium mb-2">
                                Form <span class="p-error">*</span>
                            </label>

                            <p-dropdown formControlName="formId" id="formId" class="w-full" [options]="getFormOptions()"
                                optionLabel="label" optionValue="value" placeholder="Select a form" appendTo="body"
                                [filter]="true" filterBy="label" [showClear]="true" filterPlaceholder="Search forms...">
                            </p-dropdown>

                            <div *ngIf="form.get('formId')?.invalid && form.get('formId')?.touched"
                                class="text-red-500 mt-2">
                                <small>Form selection is required.</small>
                            </div>
                        </div>
                        <div class="field col-12 md:col-6" *ngIf="form.get('id')?.value">
                            <label for="id" class="block text-900 font-medium mb-2">Assignment ID</label>
                            <input pInputText id="id" formControlName="id" class="w-full readonly-field"
                                [readonly]="true" />
                        </div>

                        <!-- Form Selection -->


                        <!-- Expires At (Update/Read Mode) -->
                        <div class="field col-12 md:col-6">
                            <label for="expiresAt" class="block text-900 font-medium mb-2">Expires At</label>
                            <input pInputText id="expiresAt" formControlName="expiresAt" class="w-full readonly-field"
                                [readonly]="true" />
                        </div>
                    </div>
                </div>

                <!-- Status & Progress Card -->
                <div class="surface-card border-round shadow-1 p-4 mb-4">
                    <div class="flex align-items-center mb-4">
                        <i class="pi pi-chart-line text-primary mr-2" style="font-size: 1.2rem;"></i>
                        <h4 class="text-900 font-semibold m-0">Status & Progress</h4>
                    </div>

                    <div class="formgrid grid">
                        <!-- Status -->
                        <div class="field col-12 md:col-6">
                            <label for="status" class="block text-900 font-medium mb-2">Status</label>
                            <p-dropdown formControlName="status" id="status" class="w-full" [options]="statusOptions"
                                optionLabel="label" optionValue="value" placeholder="Select status" appendTo="body"
                                [filter]="true" filterBy="label" [showClear]="true"
                                filterPlaceholder="Search status...">
                            </p-dropdown>
                        </div>

                        <!-- Filling Method -->
                        <div class="field col-12 md:col-6">
                            <label for="fillingMethod" class="block text-900 font-medium mb-2">Filling Method</label>
                            <input pInputText formControlName="fillingMethod" id="fillingMethod" class="w-full"
                                placeholder="Enter filling method" />
                        </div>

                        <!-- Progress -->
                        <div class="field col-12 md:col-6">
                            <label for="progress" class="block text-900 font-medium mb-2">Progress (%)</label>
                            <p-inputNumber id="progress" formControlName="progress" class="w-full" [min]="0" [max]="100"
                                suffix="%" [showButtons]="true">
                            </p-inputNumber>
                        </div>

                        <!-- Filled By User ID -->
                        <div class="field col-12 md:col-6">
                            <label for="filledByUserId" class="block text-900 font-medium mb-2">Filled By User
                                ID</label>
                            <input pInputText id="filledByUserId" formControlName="filledByUserId" class="w-full" />
                        </div>
                    </div>
                </div>

                <!-- Timeline Card -->
                <div class="surface-card border-round shadow-1 p-4 mb-4">
                    <div class="flex align-items-center mb-4">
                        <i class="pi pi-history text-primary mr-2" style="font-size: 1.2rem;"></i>
                        <h4 class="text-900 font-semibold m-0">Timeline</h4>
                    </div>

                    <div class="formgrid grid">
                        <!-- Assigned At -->
                        <div class="field col-12 md:col-4">
                            <label for="assignedAt" class="block text-900 font-medium mb-2">Assigned At</label>
                            <input pInputText id="assignedAt" formControlName="assignedAt" class="w-full readonly-field"
                                [readonly]="true" />
                        </div>

                        <!-- First Accessed At -->
                        <div class="field col-12 md:col-4">
                            <label for="firstAccessedAt" class="block text-900 font-medium mb-2">First Accessed
                                At</label>
                            <input pInputText id="firstAccessedAt" formControlName="firstAccessedAt"
                                class="w-full readonly-field" [readonly]="true" />
                        </div>

                        <!-- Completed At -->
                        <div class="field col-12 md:col-4">
                            <label for="completedAt" class="block text-900 font-medium mb-2">Completed At</label>
                            <input pInputText id="completedAt" formControlName="completedAt"
                                class="w-full readonly-field" [readonly]="true" />
                        </div>
                    </div>
                </div>

                <!-- Response Data Card -->
                <div class="surface-card border-round shadow-1 p-4 mb-4" *ngIf="form.get('responseData')?.value">
                    <div class="flex align-items-center mb-4">
                        <i class="pi pi-database text-primary mr-2" style="font-size: 1.2rem;"></i>
                        <h4 class="text-900 font-semibold m-0">Response Data</h4>
                    </div>

                    <div class="formgrid grid">
                        <div class="field col-12">
                            <label for="responseData" class="block text-900 font-medium mb-2">Response Data
                                (JSON)</label>
                            <textarea pInputTextarea id="responseData" formControlName="responseData"
                                class="w-full readonly-field" rows="6" [readonly]="true">
                            </textarea>
                        </div>
                    </div>
                </div>

            </p-accordionTab>
        </p-accordion>
    </div>

    <!-- Assignment Details Card (Visible for CREATE mode) -->
    <div class="surface-card border-round shadow-1 p-4 mb-4" *ngIf="crudMode === CrudFormAssignmentMode.CREATE">
        <div class="flex align-items-center mb-4">
            <i class="pi pi-file-edit text-primary mr-2" style="font-size: 1.2rem;"></i>
            <h4 class="text-900 font-semibold m-0">Assignment Details</h4>
        </div>

        <div class="formgrid grid">
            <!-- Form Selection -->
            <div class="field col-12 md:col-6">
                <label for="formId-create" class="block text-900 font-medium mb-2">
                    Form <span class="p-error">*</span>
                </label>

                <p-dropdown formControlName="formId" id="formId-create" class="w-full" [options]="getFormOptions()"
                    optionLabel="label" optionValue="value" placeholder="Select a form" appendTo="body" [filter]="true"
                    filterBy="label" [showClear]="true" filterPlaceholder="Search forms...">
                </p-dropdown>

                <div *ngIf="form.get('formId')?.invalid && form.get('formId')?.touched" class="text-red-500 mt-2">
                    <small>Form selection is required.</small>
                </div>
            </div>

            <!-- Duration with Presets (Create Mode) -->
            <div class="field col-12" *ngIf="form.get('durationHours')">
                <label for="durationHours" class="block text-900 font-medium mb-2">
                    Expires in (Hours) <span class="p-error">*</span>
                </label>

                <div class="grid">
                    <div class="col-12 md:col-3">
                        <p-inputNumber id="durationHours" formControlName="durationHours" class="w-full" [max]="8760"
                            [showButtons]="true" [min]="1">
                        </p-inputNumber>
                    </div>
                    <div class="col-12 md:col-9">
                        <div class="flex flex-wrap gap-2 align-items-center">
                            <p-button label="1 Hour" size="small" severity="secondary" outlined
                                (onClick)="setDurationPreset(1)">
                            </p-button>
                            <p-button label="1 Day" size="small" severity="secondary" outlined
                                (onClick)="setDurationPreset(24)">
                            </p-button>
                            <p-button label="3 Days" size="small" severity="secondary" outlined
                                (onClick)="setDurationPreset(72)">
                            </p-button>
                            <p-button label="1 Week" size="small" severity="secondary" outlined
                                (onClick)="setDurationPreset(168)">
                            </p-button>
                        </div>
                    </div>
                </div>

                <div *ngIf="form.get('durationHours')?.invalid && form.get('durationHours')?.touched"
                    class="text-red-500 mt-2">
                    <small>Duration is required and must be at least 1 hour.</small>
                </div>
            </div>
        </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex flex-wrap gap-3 justify-content-end pt-4" *ngIf="crudMode !== CrudFormAssignmentMode.READ">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" outlined (onClick)="onCancel()">
        </p-button>
        <p-button [label]="(crudMode === CrudFormAssignmentMode.CREATE ? 'Create' : 'Update') + ' Assignment'"
            icon="pi pi-check" [disabled]="form.invalid" (onClick)="onFormSubmit()">
        </p-button>
    </div>
</form>

<!-- Confirmation Dialog -->
<p-confirmDialog></p-confirmDialog>

<!-- Form Response Viewer -->
<app-form-response-viewer [(visible)]="showResponseViewer" [data]="responseViewerData" (close)="closeResponseViewer()">
</app-form-response-viewer>